import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import EvidenceUploader from "@/components/EvidenceUploader";
import DecibelMeter from "@/components/DecibelMeter";
import VideoRecorder from "@/components/VideoRecorder";
import { NOISE_SOURCES } from "@/lib/constants";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { MapPin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function NewComplaint({ controlRoomMode = false }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [zones, setZones] = useState([]);
  const [stations, setStations] = useState([]);
  const [acps, setAcps] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    noise_source: "",
    zone: "",
    police_station: "",
    address: "",
    latitude: null,
    longitude: null,
    incident_datetime: "",
    is_anonymous: false,
    evidence: [],
    decibel_reading: null,
    citizen_name: "",
    citizen_phone: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
    api.get("/acps").then((r) => setAcps(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.zone) { setStations([]); return; }
    api.get("/police-stations", { params: { zone: form.zone } })
      .then((r) => setStations(r.data))
      .catch(() => setStations([]));
  }, [form.zone]);

  const stationsByAcp = React.useMemo(() => {
    const grouped = {};
    stations.forEach((s) => {
      const key = s.acp;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(s);
    });
    return grouped;
  }, [stations]);

  const acpName = (key) => acps.find((a) => a.key === key)?.name || key;

  const useGeo = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success("Location captured");
      },
      () => toast.error("Could not get location"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.police_station) return toast.error("Please select a police station");
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.incident_datetime) {
        payload.incident_datetime = new Date(payload.incident_datetime).toISOString();
      } else {
        delete payload.incident_datetime;
      }
      if (!controlRoomMode) {
        delete payload.citizen_name;
        delete payload.citizen_phone;
      } else {
        delete payload.is_anonymous;
      }
      const { data } = await api.post("/complaints", payload);
      toast.success(`Complaint filed · Ref ${data.reference}`);
      if (controlRoomMode) nav("/control-room");
      else nav(`/citizen/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="overline text-slate-500 mb-2">
            {controlRoomMode ? "Police Control Room" : "Citizen portal"}
          </div>
          <h1 className="font-display font-bold text-3xl tracking-tight">
            {controlRoomMode ? "Register a complaint on behalf of a citizen" : "File a noise pollution complaint"}
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Auto-assigned to the Police Station Inspector. SLA: 30 min at PS → 30 min at ACP → 30 min at DCP.
          </p>
        </div>
        <Card>
          <CardHeader><CardTitle className="font-display">Complaint details</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6" data-testid="new-complaint-form">
              {controlRoomMode && (
                <div className="grid sm:grid-cols-2 gap-4 p-3 rounded-md border border-border bg-slate-50">
                  <div className="space-y-2">
                    <Label>Citizen name</Label>
                    <Input required value={form.citizen_name}
                      onChange={(e) => setForm({ ...form, citizen_name: e.target.value })}
                      data-testid="cr-citizen-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Citizen phone</Label>
                    <Input required value={form.citizen_phone}
                      onChange={(e) => setForm({ ...form, citizen_phone: e.target.value })}
                      data-testid="cr-citizen-phone-input" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Title</Label>
                <Input required maxLength={120} placeholder="e.g. Loud DJ in residential area past midnight"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  data-testid="complaint-title-input" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Noise source</Label>
                  <Select value={form.noise_source} onValueChange={(v) => setForm({ ...form, noise_source: v })}>
                    <SelectTrigger data-testid="noise-source-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {NOISE_SOURCES.map((s) => (
                        <SelectItem key={s.key} value={s.key} data-testid={`noise-source-${s.key}`}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Zone</Label>
                  <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v, police_station: "" })}>
                    <SelectTrigger data-testid="zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                    <SelectContent>
                      {zones.map((z) => (
                        <SelectItem key={z.key} value={z.key} data-testid={`zone-${z.key}`}>{z.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Police station (concerned)</Label>
                <Select
                  value={form.police_station}
                  onValueChange={(v) => setForm({ ...form, police_station: v })}
                  disabled={!form.zone}
                >
                  <SelectTrigger data-testid="ps-select">
                    <SelectValue placeholder={form.zone ? "Select police station" : "Select zone first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(stationsByAcp).map(([acpKey, list]) => (
                      <SelectGroup key={acpKey}>
                        <SelectLabel className="text-[10px] uppercase tracking-widest">{acpName(acpKey)}</SelectLabel>
                        {list.map((s) => (
                          <SelectItem key={s.key} value={s.key} data-testid={`ps-${s.key}`}>{s.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Address / landmark</Label>
                <Input required placeholder="Street, locality, nearest landmark"
                  value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="address-input" />
                <div className="flex items-center gap-3 mt-1">
                  <Button type="button" size="sm" variant="outline" onClick={useGeo} className="gap-2" data-testid="capture-gps-btn">
                    <MapPin className="w-4 h-4" /> Capture GPS
                  </Button>
                  {form.latitude && (
                    <span className="text-xs text-muted-foreground" data-testid="gps-coords">
                      {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date &amp; time of incident (optional)</Label>
                <Input type="datetime-local" value={form.incident_datetime}
                  onChange={(e) => setForm({ ...form, incident_datetime: e.target.value })}
                  data-testid="incident-datetime-input" />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea required rows={5} placeholder="Describe what's happening, duration, times of day..."
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="description-input" />
              </div>

              <div className="space-y-2">
                <Label>Live noise measurement</Label>
                <DecibelMeter
                  onComplete={({ reading, evidence }) =>
                    setForm((f) => ({
                      ...f,
                      decibel_reading: reading,
                      evidence: evidence ? [...(f.evidence || []), evidence] : f.evidence,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Live video evidence (max 2 MB)</Label>
                <VideoRecorder
                  onComplete={(evidence) => setForm((f) => ({ ...f, evidence: [...(f.evidence || []), evidence] }))}
                />
              </div>

              <div className="space-y-2">
                <Label>Additional evidence (photos / audio / video)</Label>
                <EvidenceUploader
                  value={form.evidence}
                  onChange={(v) => setForm({ ...form, evidence: v })}
                  accept="image/*,audio/*,video/*"
                />
                <p className="text-xs text-muted-foreground">Images/audio up to 10 MB. Video up to 2 MB.</p>
              </div>

              {!controlRoomMode && (
                <label className="flex items-center gap-3 p-3 rounded-md border border-border bg-slate-50 cursor-pointer">
                  <Checkbox checked={form.is_anonymous}
                    onCheckedChange={(v) => setForm({ ...form, is_anonymous: !!v })}
                    data-testid="anonymous-checkbox" />
                  <div>
                    <div className="text-sm font-medium">File anonymously</div>
                    <div className="text-xs text-muted-foreground">Your name will be hidden from officers. Track via reference number.</div>
                  </div>
                </label>
              )}

              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="cancel-btn">Cancel</Button>
                <Button type="submit" disabled={busy} data-testid="submit-complaint-btn">
                  {busy ? "Filing..." : "File Complaint"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
